import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';

import { I18nService } from '@core/i18n-legacy';

import { FooterComponent } from './components/footer/footer.component';
import { HeaderComponent, type HeaderMode } from './components/header/header.component';
import {
  MainTab,
  ProductTab,
  ProductUpdatesStateService,
} from './services/product-updates-state.service';
import { RewindService } from './services/rewind.service';
import { ThemeService } from './services/theme.service';

type StickyState = 'hidden' | 'visible' | 'hiding';

@Component({
  selector: 'pu-root',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterOutlet, HeaderComponent, FooterComponent],
  host: {
    '(window:scroll)': 'onWindowScroll()',
  },
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.less'],
})
export class AppComponent {
  // Inject ThemeService to ensure it initializes on app bootstrap
  private readonly _themeService = inject(ThemeService);
  private readonly router = inject(Router);
  private readonly translationService = inject(I18nService);
  private readonly rewindService = inject(RewindService);

  public readonly stateService = inject(ProductUpdatesStateService);

  // Hide chrome (header, footer, backgrounds) only during rewind intro phase
  public readonly rewindIntroActive = computed(
    () => this.rewindService.enabled() && this.rewindService.phase() === 'intro',
  );

  public readonly stickyState = signal<StickyState>('hidden');

  // Determine header mode based on whether we're on an article page
  // Use isArticlePage flag (set immediately) rather than currentArticle (set after data loads)
  public readonly headerMode = computed<HeaderMode>(() =>
    this.stateService.isArticlePage() ? 'article' : 'feed',
  );

  private lastScrollY = 0;
  private scrollThreshold = 260; // Hide sticky header when within purple gradient area
  private hideTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    // Check if changelog posts exist to conditionally show the changelog tab
    this.stateService.checkChangelogExists();
  }

  public onWindowScroll(): void {
    const currentScrollY = window.scrollY;
    const currentState = this.stickyState();

    // Show sticky header when scrolling up and past threshold
    if (currentScrollY < this.lastScrollY && currentScrollY > this.scrollThreshold) {
      if (this.hideTimeout) {
        clearTimeout(this.hideTimeout);
        this.hideTimeout = null;
      }
      this.stickyState.set('visible');
    } else if (currentScrollY > this.lastScrollY || currentScrollY <= this.scrollThreshold) {
      if (currentState === 'visible') {
        this.stickyState.set('hiding');
        this.hideTimeout = setTimeout(() => {
          this.stickyState.set('hidden');
          this.hideTimeout = null;
        }, 300); // Match CSS transition duration
      }
    }

    this.lastScrollY = currentScrollY;
  }

  public onMainTabChange(tab: MainTab): void {
    // Navigate to the appropriate route, preserving query params
    const path = this.getPathForMainTab(tab);
    this.router.navigate([path], { queryParams: this.buildQueryParams() });
  }

  public onProductTabChange(tab: ProductTab): void {
    // Update query params while staying on the current route
    this.router.navigate([], { queryParams: this.buildQueryParams({ product: tab }) });
  }

  public onLanguageChange(lang: string): void {
    // Update language in URL and translation service
    const url = new URL(window.location.href);
    url.searchParams.set('lang', lang);
    window.location.href = url.toString();
  }

  /**
   * Build query params object preserving current values
   */
  private buildQueryParams(
    overrides: Record<string, string | undefined> = {},
  ): Record<string, string> {
    const currentProductTab = overrides['product'] ?? this.stateService.activeProductTab();
    const currentLang = overrides['lang'] ?? this.stateService.currentLocale();
    const defaultLang = 'en';

    const params: Record<string, string> = {};

    // Only add product if not 'all'
    if (currentProductTab !== 'all') {
      params['product'] = currentProductTab;
    }

    // Only add lang if not default
    if (currentLang !== defaultLang) {
      params['lang'] = currentLang;
    }

    return params;
  }

  private getPathForMainTab(tab: MainTab): string {
    switch (tab) {
      case 'coming-soon':
        return '/coming-soon';
      case 'changelog':
        return '/changelog';
      case 'timeline':
        return '/timeline';
      case 'whats-new':
      default:
        return '/';
    }
  }
}
